import { type ReadyStateEvent, type SSEvent, SSE } from "sse.js";

import { v1BaseUrl } from "shared/constants";
import StorageService from "shared/services/StorageService";
import { type Nullable } from "shared/types";

interface ServerSentEventOptions<T> {
  url: string;
  payload?: string;
  onSuccess: (payload: T) => void;
  onError: (error: ErrorResponse) => void;
  onStreamStart?: () => void;
  onStreamEnd?: () => void;
  start?: boolean;
}

type StreamingResponseData<T> = T & {
  error?: string;
};

interface StreamingResponse<T> {
  data: StreamingResponseData<T>;
  response_code: number;
}

export interface ErrorResponse {
  message: string;
  code: number;
}

export default async function useServerSentEvents<T>(
  options: ServerSentEventOptions<T>
) {
  const token = await StorageService.get("token");
  let error: Nullable<ErrorResponse> = null;

  const eventSource = new SSE(`${v1BaseUrl}/${options.url}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    payload: options.payload,
    start: options.start,
  });

  eventSource.onmessage = (event: SSEvent) => {
    const payload: StreamingResponse<T> = JSON.parse(event.data);

    if (payload.response_code === 200) {
      options.onSuccess(payload.data);
    } else if (payload.data.error) {
      error = { message: payload.data.error, code: payload.response_code };
    }
  };

  eventSource.onerror = (event: SSEvent) => {
    options.onError({
      message: event.data,
      code: 521,
    });
  };

  eventSource.onreadystatechange = (event: ReadyStateEvent) => {
    if (event.readyState === SSE.OPEN) {
      if (options.onStreamStart) options.onStreamStart();
    } else if (event.readyState === SSE.CLOSED) {
      if (error) {
        options.onError(error);
      }

      if (options.onStreamEnd) options.onStreamEnd();
    }
  };

  return {
    eventSource,
  };
}
